import { SCOPES } from './../../../../services/scope/scope-constants';
import { displayName, optionalName } from '../../../../validators/name-rules';
import { Component, computed, inject, LOCALE_ID, signal , DestroyRef} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { FilterInput } from '../../../../shared/ui/shared-filters/filter-input/filter-input';
import {
  arabicOnlyValidator,
  englishOnlyValidator,
  noLeadingTrailingSpacesValidator,
  noNumbersValidator,
} from '../cancellation-reason.validators';
import { ConfirmationDialog } from '../../../../shared/ui/confirmation-dialog/confirmation-dialog';
import { SuccessfulNotification } from "../../../../utils/successful-notification/successful-notification";
import { Loading } from "../../../../utils/loading/loading";
import { CancellationReasons } from '../../../../services/Cancellation Reasons/cancellation-reasons.service';
import { CancellationReasonError } from '../../../../pages/admin-dashboard/cancellation-reasons/cancellationReason.model';
import { SubmitButton } from "../../../../shared/ui/Buttons/submit-button/submit-button";
import { HasScopeDirective } from '../../../../services/scope/has-scope.directive';
import { ScopeService } from '../../../../services/scope/scope-service';


@Component({
  selector: 'app-cancellation-reasons-create-component',
  imports: [FilterInput, ReactiveFormsModule, ConfirmationDialog, SuccessfulNotification, Loading, SubmitButton , HasScopeDirective],
  templateUrl: './cancellation-reasons-create-component.html',
  styleUrl: './cancellation-reasons-create-component.scss',
})
export class CancellationReasonsCreateComponent {
  private router = inject(Router);
  private locale = inject(LOCALE_ID);
  private cancellationReasonsService = inject(CancellationReasons);
  private destroyRef = inject(DestroyRef);
  readonly SCOPES =SCOPES;

  isArabic = computed(() => this.locale.startsWith('ar'));
  isLoading = signal(false);

  titleArLabel = $localize`:@@cancellationReasonTitleArabic:Reason Title (Arabic)`;
  titleArPlaceholder = $localize`:@@cancellationReasonTitleArabicPlaceholder:Enter Arabic reason`;

  titleEnLabel = $localize`:@@cancellationReasonTitleEnglish:Reason Title (English)`;
  titleEnPlaceholder = $localize`:@@cancellationReasonTitleEnglishPlaceholder:Enter English reason (optional)`;

  cancelButton = $localize`:@@cancellationReasonCancel:Cancel`;
  submitButton = $localize`:@@cancellationReasonsubmit:submit`;
  Loading = $localize`:@@cancellationReasonLoading:Loading...`;
  showConfirmation = signal(false);

  confirmationTitle = $localize`:@@cancellationReasonCreateConfirmationTitle:Confirm Creation`;

  confirmationDescription = $localize`:@@cancellationReasonCreateConfirmation:Are you sure you want to create this reason?`;

  confirmButton = signal(
  $localize`:@@cancellationReasonConfirm:Confirm`
);
  

  titleAr = computed(() => this.titleArControl().value ?? '');
  titleEn = computed(() => this.titleEnControl().value ?? '');

  cancellationReasonForm = new FormGroup({
    titleAr: new FormControl('', [
      Validators.required,
      Validators.minLength(3),
      Validators.maxLength(255),
      arabicOnlyValidator(),
      noLeadingTrailingSpacesValidator(),
      noNumbersValidator(),
    ]),

    titleEn: new FormControl('', [
      Validators.minLength(3),
      Validators.maxLength(255),
      englishOnlyValidator(),
      noLeadingTrailingSpacesValidator(),
      noNumbersValidator(),
    ]),
  });

  onArabicChange(value: string | number | null) {
    this.titleArControl().setValue(value?.toString() ?? '');
    this.titleArControl().markAsTouched();
    this.titleArControl().updateValueAndValidity();
  }

  onEnglishChange(value: string | number | null) {
    this.titleEnControl().setValue(value?.toString() ?? '');
    this.titleEnControl().markAsTouched();
    this.titleEnControl().updateValueAndValidity();
  }

  submitForm() {

  this.cancellationReasonForm.markAllAsTouched();

  if (this.cancellationReasonForm.invalid) {
    return;
  }
this.showConfirmation.set(true);
}


  cancel() {
    this.router.navigate(['/admin/cancellation']);
  }

readonly titleArControl = computed(
  () => this.cancellationReasonForm.controls.titleAr,
);

readonly titleEnControl = computed(
  () => this.cancellationReasonForm.controls.titleEn,
);


  successMessage = signal<string | null>(null);
  // errorMessage = signal<string | null>(null);
  errorMessage = signal('');

  confirmCreate() {
  this.isLoading.set(true);
  this.confirmButton.set(this.Loading);

  const data = this.cancellationReasonForm.getRawValue();

  const dto = {
    titleEn: optionalName(data.titleEn),
    titleAr: data.titleAr ?? '',
  };

  console.log('Request body:', dto);

 const subscription = this.cancellationReasonsService.createCancellationReason(dto).subscribe({
    next: () => {
      this.isLoading.set(false);
      this.confirmButton.set(
            $localize`:@@cancellationReasonConfirm:Confirm`
          );
      this.showConfirmation.set(false);
      this.errorMessage.set('');

      const reasonName = displayName(this.isArabic(), dto.titleAr, dto.titleEn);

      this.successMessage.set(
        `${$localize`:@@cancellationReasonCreatedSuccessfully:Cancellation reason`} "${reasonName}" ${$localize`:@@cancellationReasonCreatedSuccessfully2:created successfully`}`,
      );

      setTimeout(() => {
        this.router.navigate(['/admin/cancellation']);
      }, 3000);
    },

error: (error: CancellationReasonError) => {
  this.isLoading.set(false);
  this.confirmButton.set(
            $localize`:@@cancellationReasonConfirm:Confirm`
          );

  if (error.code === 'REST-0004' && error.reqBodyErrors) {
    const hasArabicError = error.reqBodyErrors.some(error =>
      error.includes('Field: titleAr'),
    );

    const hasEnglishError = error.reqBodyErrors.some(error =>
      error.includes('Field: titleEn'),
    );

    if (hasArabicError && hasEnglishError) {
      this.errorMessage.set(
        this.isArabic()
          ? 'اسم السبب باللغة العربية والإنجليزية غير صالح.'
          : 'The Arabic and English reason names are invalid.',
      );

      return;
    }

    if (hasArabicError) {
      this.errorMessage.set(
        this.isArabic()
          ? 'اسم السبب باللغة العربية غير صالح.'
          : 'The Arabic reason name is invalid.',
      );

      return;
    }

    if (hasEnglishError) {
      this.errorMessage.set(
        this.isArabic()
          ? 'اسم السبب باللغة الإنجليزية غير صالح.'
          : 'The English reason name is invalid.',
      );

      return;
    }
  }

  this.errorMessage.set(
    this.isArabic()
      ? error.messageAr || error.messageEn
      : error.messageEn || error.messageAr,
  );
},
  });
  this.destroyRef.onDestroy( () => subscription.unsubscribe());
}

  closeConfirmation() {
    this.showConfirmation.set(false);
  }
}
